import { Navigate, Outlet, useRoutes } from 'react-router';
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import AdminLayout from '../layouts/admin';
import ResearchLayout from '../layouts/research';
import LogoOnlyLayout from '../layouts/logo-only';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import OrgMemberGuard from '../guards/OrgMemberGuard';
import { ADMIN_ROLES } from '../common/adminRoles';
import LightModeTheme from '../theme/LightModeTheme';
import ContextAwareOverviewRedirect from './ContextAwareOverviewRedirect';
import {
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  NewPasswordPage,
  VerifyCodePage,
  PersonalLearningPage,
  UpNextPage,
  TopicsPage,
  FlashcardReviewPage,
  NotificationsPage,
  PathwayBuilderPage,
  PathwayMapPage,
  PathwayCreatePage,
  PathwaysPage,
  TopicEditSinglePage,
  ImmersivePathwayPage,
  ExplorePathwaysPage,
  ExplorePathwayViewPage,
  ExplorePathwayFollowingPage,
  ExplorePathwayFollowingViewerPage,
  ExplorePathwaySharedPage,
  ExplorePathwaySharedViewerPage,
  SuggestionsPage,
  CirclesPage,
  CircleCreatePage,
  CircleDetailsPage,
  CircleActivityPage,
  AIContentPage,
  ExternalPlatformsPage,
  UserAccountPage,
  ReportProblemPage,
  AdminPage,
  AdminOrganizationsPage,
  AdminOrganizationDetailPage,
  TopicMigrationPage,
  ResourceMigrationPage,
  LearningCyclesPage,
  ResearchOverviewPage,
  OrgOverviewPage,
  OrgMembersPage,
  OrgCirclesPage,
  OrgCircleCreatePage,
  OrgPathwaysPage,
  OrgPathwayCreatePage,
  InvitationPage,
  LandingPage,
  TermsPage,
  PrivacyPolicyPage,
  ComingSoonPage,
  MaintenancePage,
  Page500,
  NotFoundPage
} from './pages';

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      element: (
        <LightModeTheme>
          <Outlet />
        </LightModeTheme>
      ),
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          )
        },
        {
          path: 'reset-password',
          element: (
            <GuestGuard>
              <ResetPasswordPage />
            </GuestGuard>
          )
        },
        {
          path: 'new-password',
          element: (
            <GuestGuard>
              <NewPasswordPage />
            </GuestGuard>
          )
        },
        {
          path: 'verify',
          element: (
            <GuestGuard>
              <VerifyCodePage />
            </GuestGuard>
          )
        }
      ]
    },
    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <ContextAwareOverviewRedirect /> },
        {
          path: 'pathway',
          children: [
            { path: 'new', element: <PathwayCreatePage /> },
            { path: ':title/immersive', element: <ImmersivePathwayPage /> },
            { path: ':title/map', element: <PathwayMapPage /> },
            { path: ':title', element: <PathwayBuilderPage /> },
            { path: ':title/:topicGuid', element: <PathwayBuilderPage /> },
            { path: 'edit/:pathwayId', element: <PathwayCreatePage /> }
          ]
        },
        {
          path: 'pathways',
          children: [{ path: 'view', element: <PathwaysPage /> }]
        },
        {
          path: 'explore',
          children: [
            { path: 'pathways', element: <ExplorePathwaysPage /> },
            { path: 'pathways/:slug', element: <ExplorePathwayViewPage /> },
            { path: 'following', element: <ExplorePathwayFollowingPage /> },
            { path: 'followed/:slug', element: <ExplorePathwayFollowingViewerPage /> },
            { path: 'sharedWithYou', element: <ExplorePathwaySharedPage /> },
            { path: 'shared/:slug', element: <ExplorePathwaySharedViewerPage /> },
            { path: 'suggestions', element: <SuggestionsPage /> },
            { path: 'ai-content', element: <AIContentPage /> }
          ]
        },
        {
          path: 'overview',
          children: [{ path: '', element: <PersonalLearningPage /> }]
        },
        { path: 'flashcards', element: <FlashcardReviewPage /> },
        { path: 'notifications', element: <NotificationsPage /> },
        {
          path: 'up-next',
          children: [{ path: '', element: <UpNextPage /> }]
        },
        { path: 'topics', element: <TopicsPage /> },
        {
          path: 'completed-topics',
          element: <Navigate to="/dashboard/topics?filter=completed" replace />
        },
        { path: 'topic/:pathwaySlug/:topicGuid/view', element: <TopicEditSinglePage /> },
        {
          path: 'circle',
          children: [
            { path: '', element: <CirclesPage /> },
            { path: 'create', element: <CircleCreatePage /> },
            { path: 'activity', element: <CircleActivityPage /> },
            { path: ':circleId', element: <CircleDetailsPage /> },
            { path: ':circleId/ai-content', element: <AIContentPage /> }
          ]
        },
        { path: 'external-platforms', element: <ExternalPlatformsPage /> },
        {
          path: 'user',
          children: [
            { path: '', element: <Navigate to="/dashboard/user/account" replace /> },
            { path: 'account', element: <UserAccountPage /> },
            { path: 'report', element: <ReportProblemPage /> }
          ]
        }
      ]
    },
    // Admin Console Routes
    {
      path: 'admin',
      element: (
        <AuthGuard>
          <RoleBasedGuard accessibleRoles={ADMIN_ROLES}>
            <AdminLayout />
          </RoleBasedGuard>
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Navigate to="/admin/analytics" replace /> },
        { path: 'analytics', element: <AdminPage /> },
        { path: 'organizations', element: <AdminOrganizationsPage /> },
        { path: 'organizations/:guid', element: <AdminOrganizationDetailPage /> },
        { path: 'topic-migration', element: <TopicMigrationPage /> },
        { path: 'resource-migration', element: <ResourceMigrationPage /> },
        { path: 'learning-cycles', element: <LearningCyclesPage /> }
      ]
    },

    // Organization Console Routes
    {
      path: 'organization',
      element: (
        <AuthGuard>
          <OrgMemberGuard>
            <DashboardLayout />
          </OrgMemberGuard>
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Navigate to="/organization/overview" replace /> },
        { path: 'overview', element: <OrgOverviewPage /> },
        { path: 'members', element: <OrgMembersPage /> },
        { path: 'circles', element: <OrgCirclesPage /> },
        { path: 'circles/create', element: <OrgCircleCreatePage /> },
        { path: 'pathways', element: <OrgPathwaysPage /> },
        { path: 'pathways/create', element: <OrgPathwayCreatePage /> },
        { path: 'settings', element: <Navigate to="/organization/overview" replace /> }
      ]
    },

    // Research Routes
    {
      path: 'research',
      element: (
        <AuthGuard>
          <RoleBasedGuard accessibleRoles={ADMIN_ROLES}>
            <ResearchLayout />
          </RoleBasedGuard>
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Navigate to="/research/overview" replace /> },
        { path: 'overview', element: <ResearchOverviewPage /> }
      ]
    },

    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFoundPage /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      // Landing page: GuestGuard wraps the layout so MainNavbar never mounts
      // for authenticated users or while auth is still initializing.
      path: '',
      element: (
        <GuestGuard>
          <MainLayout />
        </GuestGuard>
      ),
      children: [{ path: '', element: <LandingPage /> }]
    },
    {
      // Public info pages: no auth redirect needed.
      path: '',
      element: <MainLayout />,
      children: [
        { path: 'terms', element: <TermsPage /> },
        { path: 'privacy-policy', element: <PrivacyPolicyPage /> },
        { path: 'invitation/:token', element: <InvitationPage /> }
      ]
    }
  ]);
}
